'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';

// ── TYPEWRITER ────────────────────────────────────────────────────────────────
function useTypewriter(words: string[], interval = 2600) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (words.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);
  return { word: words[idx], idx };
}

// ── CENA: VIDEO MEET ──────────────────────────────────────────────────────────
const PARTICIPANTS = [
  { name: 'Ana Souza',  initials: 'AS', color: '#3b82f6', mic: true,  cam: true,  delay: 0,   active: false },
  { name: 'Bruno M.',   initials: 'BM', color: '#F59E0B', mic: true,  cam: true,  delay: 80,  active: true  },
  { name: 'Carlos R.',  initials: 'CR', color: '#a855f7', mic: false, cam: true,  delay: 160, active: false },
  { name: '+ 5 outros', initials: '+5', color: '#374151', mic: false, cam: false, delay: 240, active: false, extra: true },
];

function MicIcon({ active }: { active: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke={active ? 'rgba(255,255,255,0.7)' : '#ef4444'} strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      {!active && <line x1="2" y1="2" x2="22" y2="22"/>}
    </svg>
  );
}

function CamIcon({ active }: { active: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke={active ? 'rgba(255,255,255,0.7)' : '#ef4444'} strokeWidth="2.5" strokeLinecap="round">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      {!active && <line x1="2" y1="2" x2="22" y2="22"/>}
    </svg>
  );
}

function VideoMeetScene() {
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '520px', perspective: '1400px' }}>
      <div style={{
        position: 'relative', margin: '0 auto', height: '340px', width: '100%',
        transform: 'rotateX(14deg) rotateY(-18deg) rotateZ(2deg)',
        transformStyle: 'preserve-3d',
      }}>

        {/* FloatingCard: participantes (topo direito) */}
        <div style={{
          position: 'absolute', zIndex: 30, top: '-8px', right: '0', width: '190px',
          borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)',
          background: 'linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))',
          padding: '14px', backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
          animation: 'loginFloatSlow 7s ease-in-out infinite',
          transform: 'translateZ(60px)',
        }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.1em', color:'rgba(255,255,255,0.5)' }}>
            <span>Participantes</span>
            <span style={{ color:'#34d399', fontSize:'10px' }}>● Ao vivo</span>
          </div>
          <div style={{ marginTop:'6px', fontSize:'24px', fontWeight:700 }}>8</div>
          <div style={{ marginTop:'2px', fontSize:'11px', color:'#34d399', fontWeight:500 }}>+2 aguardando</div>
          <div style={{ marginTop:'12px', display:'flex', height:'32px', alignItems:'flex-end', gap:'3px' }}>
            {[30,55,40,70,50,85,60,90,45,75].map((h, i) => (
              <div key={i} style={{
                flex:1, borderRadius:'2px',
                background:'linear-gradient(to top,rgba(245,158,11,0.4),#F59E0B)',
                height:`${h}%`,
                // @ts-ignore
                '--bar-h':`${h}%`,
                animation:`loginBarGrow 900ms ${i*60}ms ease-out both`,
              }}/>
            ))}
          </div>
        </div>

        {/* FloatingCard: duração da reunião (baixo esquerdo) */}
        <div style={{
          position:'absolute', zIndex:30, bottom:'-16px', left:'-8px', width:'200px',
          borderRadius:'16px', border:'1px solid rgba(255,255,255,0.1)',
          background:'linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))',
          padding:'14px', backdropFilter:'blur(20px)',
          boxShadow:'0 25px 50px -12px rgba(0,0,0,0.5)',
          animation:'loginFloat 6s ease-in-out infinite',
          animationDelay:'1.2s', transform:'translateZ(70px)',
        }}>
          <div style={{ fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.1em', color:'rgba(255,255,255,0.5)' }}>Reunião em curso</div>
          <div style={{ marginTop:'6px', display:'flex', alignItems:'baseline', gap:'8px' }}>
            <span style={{ fontSize:'22px', fontWeight:700, fontVariantNumeric:'tabular-nums' }}>42:18</span>
            <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)' }}>duração</span>
          </div>
          <div style={{ marginTop:'10px', height:'4px', background:'rgba(255,255,255,0.1)', borderRadius:'9999px', overflow:'hidden' }}>
            <div style={{ height:'100%', width:'65%', borderRadius:'9999px', background:'linear-gradient(to right,#F59E0B,#fbbf24)', boxShadow:'0 0 12px rgba(245,158,11,0.6)' }}/>
          </div>
          <div style={{ marginTop:'10px', display:'flex' }}>
            {['A','B','C','L'].map((c,i) => (
              <div key={i} style={{
                width:'22px', height:'22px', borderRadius:'50%',
                border:'1.5px solid hsl(222,47%,8%)',
                background:['#3b82f6','#F59E0B','#a855f7','#10b981'][i],
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:'9px', fontWeight:600, color:'white',
                marginLeft: i > 0 ? '-8px' : '0',
              }}>{c}</div>
            ))}
            <div style={{ marginLeft:'-8px', width:'22px', height:'22px', borderRadius:'50%', border:'1.5px solid hsl(222,47%,8%)', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:600 }}>+4</div>
          </div>
        </div>

        {/* Grade de vídeo principal */}
        <div style={{
          position:'relative', zIndex:10, height:'100%',
          display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px',
          borderRadius:'20px', border:'1px solid rgba(255,255,255,0.1)',
          background:'rgba(255,255,255,0.025)', padding:'10px',
          backdropFilter:'blur(20px)',
        }}>
          {PARTICIPANTS.map((p, i) => (
            <div key={i} style={{
              position:'relative', borderRadius:'12px',
              border: p.active ? '1px solid rgba(245,158,11,0.6)' : '1px solid rgba(255,255,255,0.08)',
              background: p.active ? 'rgba(245,158,11,0.08)' : (p.extra ? 'rgba(255,255,255,0.01)' : 'rgba(255,255,255,0.04)'),
              padding:'10px', display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center', overflow:'hidden',
              boxShadow: p.active ? '0 8px 30px -6px rgba(245,158,11,0.4)' : 'none',
              animation:`loginRise 700ms ${p.delay}ms ease-out both`,
            }}>
              {p.active && (
                <div style={{ position:'absolute', inset:0, overflow:'hidden', borderRadius:'12px', pointerEvents:'none' }}>
                  <div style={{ position:'absolute', top:0, bottom:0, width:'33%', background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)', animation:'loginShine 3.4s ease-in-out infinite' }}/>
                </div>
              )}
              <div style={{
                width:'40px', height:'40px', borderRadius:'50%',
                background: p.extra ? 'rgba(255,255,255,0.06)' : p.color,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize: p.extra ? '13px' : '14px', fontWeight:700, color:'white',
                border: p.active ? '2px solid rgba(245,158,11,0.8)' : '2px solid rgba(255,255,255,0.1)',
              }}>{p.initials}</div>
              <div style={{
                marginTop:'6px', fontSize:'10px', fontWeight:600,
                color: p.extra ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.9)',
                textAlign:'center', maxWidth:'100%', overflow:'hidden',
                textOverflow:'ellipsis', whiteSpace:'nowrap',
              }}>{p.name}</div>
              {!p.extra && (
                <div style={{ marginTop:'5px', display:'flex', gap:'6px', alignItems:'center' }}>
                  <MicIcon active={p.mic}/>
                  <CamIcon active={p.cam}/>
                  {p.active && (
                    <span style={{ fontSize:'8px', padding:'1px 5px', borderRadius:'4px', background:'rgba(245,158,11,0.2)', color:'#fbbf24', fontWeight:600 }}>HOST</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── FEATURES ──────────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: '🔒', title: 'Criptografado', subtitle: 'E2E disponível'    },
  { icon: '⚡', title: 'Baixa latência', subtitle: 'WebRTC nativo'    },
  { icon: '🖥️', title: 'Compartilhe',   subtitle: 'Tela em HD'        },
];

const WORDS = ['produtiva.', 'segura.', 'eficiente.', 'ao vivo.'];

// ── TOGGLE ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div onClick={onChange} style={{
      width:'36px', height:'20px', borderRadius:'10px', flexShrink:0,
      background: checked ? '#F59E0B' : 'rgba(0,0,0,0.15)',
      position:'relative', cursor:'pointer', transition:'background 0.2s',
    }}>
      <div style={{
        position:'absolute', top:'2px', left: checked ? '18px' : '2px',
        width:'16px', height:'16px', borderRadius:'50%', background:'white',
        transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
      }}/>
    </div>
  );
}

// ── HOME ──────────────────────────────────────────────────────────────────────
function HomeContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [tab, setTab] = useState<'start'|'join'>(
    searchParams?.get('tab') === 'join' ? 'join' : 'start'
  );
  const [roomId, setRoomId]   = useState('');
  const [e2ee, setE2ee]       = useState(false);
  const [passphrase]          = useState(randomString(64));
  const { word, idx }         = useTypewriter(WORDS);

  const [showPwModal, setShowPwModal] = useState(false);
  const [pwInput, setPwInput]         = useState('');
  const [pwError, setPwError]         = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [meetingMode, setMeetingMode]   = useState<'instant'|'later'>('instant');
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied]       = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const startMeeting = (mode: 'instant'|'later') => {
    setMeetingMode(mode);
    setShowDropdown(false);
    setShowPwModal(true);
    setPwInput('');
    setPwError(false);
    if (mode === 'instant') {
      setScheduledDate('');
      setScheduledTime('');
    }
  };

  const [scheduleError, setScheduleError] = useState(false);

  const confirmStart = async () => {
    if (pwInput !== 'MBM@2026@Meet') {
      setPwError(true);
      return;
    }
    if (meetingMode === 'later' && (!scheduledDate || !scheduledTime)) {
      setScheduleError(true);
      return;
    }
    setShowPwModal(false);
    setScheduleError(false);

    // Create room with signed expiration via server
    const fetchBody: Record<string, unknown> = {};
    if (meetingMode === 'later' && scheduledDate && scheduledTime) {
      fetchBody.scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`).getTime();
    }
    const resp = await fetch('/api/room/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fetchBody),
    });
    const { roomName: id, exp, sig } = await resp.json();

    const expParams = `exp=${exp}&sig=${sig}`;

    if (meetingMode === 'later') {
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const link = e2ee
        ? `${base}/rooms/${id}?${expParams}#${encodePassphrase(passphrase)}`
        : `${base}/rooms/${id}?${expParams}`;
      setGeneratedLink(link);
      setLinkCopied(false);
    } else {
      const hash = e2ee ? `#${encodePassphrase(passphrase)}` : '';
      router.push(`/rooms/${id}?role=admin&${expParams}${hash}`);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const joinMeeting = (e: React.FormEvent) => {
    e.preventDefault();
    const id = roomId.trim();
    if (!id) return;
    router.push(e2ee ? `/rooms/${id}#${encodePassphrase(passphrase)}` : `/rooms/${id}`);
  };

  const btnStyle: React.CSSProperties = {
    width:'100%', padding:'12px', borderRadius:'10px', border:'none',
    cursor:'pointer', fontSize:'14px', fontWeight:700,
    background:'linear-gradient(135deg,#F59E0B,#fbbf24)', color:'#000',
    boxShadow:'0 4px 20px rgba(245,158,11,0.4)', transition:'opacity 0.2s',
  };

  return (
    <div className="mbm-home-grid" style={{
      display:'grid', minHeight:'100vh', width:'100%',
      gridTemplateColumns:'100%', overflow:'hidden',
    }}>

      {/* ── ESQUERDA: FORMULÁRIO (fundo claro) ── */}
      <div style={{
        position:'relative', display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
        background:'#ffffff', padding:'32px 24px', overflow:'hidden',
      }}>
        <div style={{ position:'relative', zIndex:10, width:'100%', maxWidth:'380px' }}>

          {/* Header */}
          <div style={{ marginBottom:'32px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <img src="/logo-mbm.png" alt="MBM Meet" style={{ height:'36px', width:'auto', userSelect:'none', filter:'brightness(0)' }}/>
            <div style={{
              display:'inline-flex', alignItems:'center', gap:'8px',
              borderRadius:'9999px', border:'1px solid rgba(0,0,0,0.1)',
              background:'rgba(0,0,0,0.03)', padding:'6px 12px',
              fontSize:'12px', fontWeight:500,
            }}>
              <span style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#34d399', animation:'loginPulseDot 2s ease-in-out infinite', display:'inline-block' }}/>
              <span style={{ color:'rgba(0,0,0,0.7)' }}>Sistema online</span>
            </div>
          </div>

          {/* Título */}
          <div style={{ marginBottom:'24px' }}>
            <h1 style={{ fontSize:'26px', fontWeight:700, letterSpacing:'-0.02em', color:'#1a1a1a', margin:0, lineHeight:1.2 }}>
              Bem-vindo ao<br/>
              <span style={{ color:'#F59E0B' }}>MBM Meet</span>
            </h1>
            <p style={{ marginTop:'8px', fontSize:'13px', color:'rgba(0,0,0,0.5)', lineHeight:1.6, margin:'8px 0 0' }}>
              Reuniões seguras e em tempo real para a sua equipe.
            </p>
          </div>

          {/* Abas */}
          <div style={{
            display:'flex', gap:'4px', borderRadius:'12px',
            background:'rgba(0,0,0,0.04)', border:'1px solid rgba(0,0,0,0.08)',
            padding:'4px', marginBottom:'16px',
          }}>
            {(['start','join'] as const).map((t) => (
              <button key={t} onClick={() => { setTab(t); setGeneratedLink(''); }} style={{
                flex:1, padding:'9px', borderRadius:'8px', border:'none', cursor:'pointer',
                fontSize:'13px', fontWeight:600, transition:'all 0.2s',
                background: tab===t ? '#F59E0B' : 'transparent',
                color: tab===t ? '#000' : 'rgba(0,0,0,0.5)',
              }}>
                {t==='start' ? 'Nova reunião' : 'Entrar na sala'}
              </button>
            ))}
          </div>

          {/* Card de conteúdo */}
          <div style={{
            borderRadius:'16px', border:'1px solid rgba(0,0,0,0.08)',
            background:'rgba(0,0,0,0.02)', padding:'20px',
          }}>
            {tab==='start' ? (
              <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
                <p style={{ margin:0, fontSize:'13px', color:'rgba(0,0,0,0.5)', lineHeight:1.6 }}>
                  Crie uma nova sala e compartilhe o link com os participantes.
                </p>
                <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
                  <Toggle checked={e2ee} onChange={() => setE2ee(!e2ee)}/>
                  <span style={{ fontSize:'12px', color:'rgba(0,0,0,0.6)' }}>Criptografia ponta a ponta</span>
                </label>

                {/* Botão com dropdown */}
                <div ref={dropdownRef} style={{ position:'relative' }}>
                  <div style={{ display:'flex', gap:'0' }}>
                    <button onClick={() => startMeeting('instant')} style={{
                      ...btnStyle, borderRadius:'10px 0 0 10px', width:'auto', flex:1,
                    }}>Iniciar reunião</button>
                    <button onClick={() => setShowDropdown(!showDropdown)} style={{
                      padding:'12px 14px', borderRadius:'0 10px 10px 0', border:'none',
                      cursor:'pointer', fontSize:'14px', fontWeight:700,
                      background:'linear-gradient(135deg,#e8920a,#f0ad30)', color:'#000',
                      borderLeft:'1px solid rgba(0,0,0,0.15)',
                      display:'flex', alignItems:'center',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                  </div>

                  {showDropdown && (
                    <div style={{
                      position:'absolute', top:'calc(100% + 6px)', left:0, right:0,
                      borderRadius:'12px', border:'1px solid rgba(0,0,0,0.1)',
                      background:'#fff', padding:'6px',
                      boxShadow:'0 10px 40px rgba(0,0,0,0.12)',
                      zIndex:50,
                    }}>
                      <button onClick={() => startMeeting('later')} style={{
                        width:'100%', padding:'10px 12px', borderRadius:'8px', border:'none',
                        background:'transparent', cursor:'pointer', display:'flex',
                        alignItems:'center', gap:'10px', fontSize:'13px', color:'#1a1a1a',
                        textAlign:'left',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.04)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                        Criar reunião para depois
                      </button>
                      <button onClick={() => startMeeting('instant')} style={{
                        width:'100%', padding:'10px 12px', borderRadius:'8px', border:'none',
                        background:'transparent', cursor:'pointer', display:'flex',
                        alignItems:'center', gap:'10px', fontSize:'13px', color:'#1a1a1a',
                        textAlign:'left',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.04)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Iniciar reunião instantânea
                      </button>
                    </div>
                  )}
                </div>

                {/* Link gerado */}
                {generatedLink && (
                  <div style={{
                    borderRadius:'12px', border:'1px solid rgba(245,158,11,0.3)',
                    background:'rgba(245,158,11,0.06)', padding:'14px',
                  }}>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(0,0,0,0.6)', marginBottom:'8px' }}>
                      Link da reunião criado
                      {scheduledDate && scheduledTime && (
                        <span style={{ fontWeight:400, color:'rgba(0,0,0,0.45)' }}>
                          {' '}— agendada para {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })} às {scheduledTime}
                        </span>
                      )}:
                    </div>
                    <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                      <input
                        readOnly
                        value={generatedLink}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                        style={{
                          flex:1, padding:'8px 12px', borderRadius:'8px',
                          border:'1px solid rgba(0,0,0,0.1)', background:'#fff',
                          color:'#1a1a1a', fontSize:'12px', outline:'none',
                          fontFamily:'monospace', boxSizing:'border-box',
                        }}
                      />
                      <button onClick={copyLink} style={{
                        padding:'8px 14px', borderRadius:'8px', border:'none',
                        background: linkCopied ? '#34d399' : '#F59E0B',
                        color: linkCopied ? '#fff' : '#000',
                        fontSize:'12px', fontWeight:600, cursor:'pointer',
                        whiteSpace:'nowrap', transition:'all 0.2s',
                      }}>
                        {linkCopied ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={joinMeeting} style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
                <p style={{ margin:0, fontSize:'13px', color:'rgba(0,0,0,0.5)', lineHeight:1.6 }}>
                  Digite o código da sala para entrar na reunião.
                </p>
                <input
                  type="text"
                  placeholder="Código da sala (ex: abc-defg-hij)"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                  style={{
                    width:'100%', padding:'10px 14px', borderRadius:'10px',
                    border:'1px solid rgba(0,0,0,0.12)', background:'#fff',
                    color:'#1a1a1a', fontSize:'13px', outline:'none', boxSizing:'border-box',
                    fontFamily:'inherit',
                  }}
                />
                <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
                  <Toggle checked={e2ee} onChange={() => setE2ee(!e2ee)}/>
                  <span style={{ fontSize:'12px', color:'rgba(0,0,0,0.6)' }}>Criptografia ponta a ponta</span>
                </label>
                <button type="submit" style={btnStyle}>Entrar na reunião</button>
              </form>
            )}
          </div>

        </div>
      </div>

      {/* ── DIREITA: PAINEL BRANDED (desktop) ── */}
      <aside className="mbm-panel-right" style={{
        position:'relative', flexDirection:'column', justifyContent:'space-between',
        overflow:'hidden', background:'hsl(222,47%,6%)', padding:'48px', color:'white',
      }}>
        {/* Grade animada */}
        <div style={{
          pointerEvents:'none', position:'absolute', inset:0, opacity:0.18,
          backgroundImage:'linear-gradient(to right,rgba(255,255,255,0.08) 1px,transparent 1px),linear-gradient(to bottom,rgba(255,255,255,0.08) 1px,transparent 1px)',
          backgroundSize:'44px 44px',
          maskImage:'radial-gradient(ellipse at 60% 45%,black 35%,transparent 78%)',
          animation:'loginGridDrift 18s linear infinite',
        }}/>
        {/* Glows */}
        <div style={{ pointerEvents:'none', position:'absolute', top:'-160px', right:'-128px', width:'520px', height:'520px', borderRadius:'50%', background:'rgba(245,158,11,0.2)', filter:'blur(120px)' }}/>
        <div style={{ pointerEvents:'none', position:'absolute', bottom:'-128px', left:'-80px', width:'420px', height:'420px', borderRadius:'50%', background:'rgba(167,139,250,0.18)', filter:'blur(120px)' }}/>
        <div style={{ pointerEvents:'none', position:'absolute', top:'33%', left:'50%', transform:'translateX(-50%)', width:'260px', height:'260px', borderRadius:'50%', background:'rgba(56,189,248,0.12)', filter:'blur(100px)' }}/>

        {/* Chips do topo */}
        <div style={{ position:'relative', zIndex:10, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'8px', borderRadius:'9999px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', padding:'4px 12px', fontSize:'12px', fontWeight:500, backdropFilter:'blur(8px)' }}>
            ✦ MBM Meet
          </span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:'8px', borderRadius:'9999px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', padding:'4px 12px', fontSize:'12px', fontWeight:500, backdropFilter:'blur(8px)' }}>
            <span style={{ position:'relative', display:'inline-flex', width:'8px', height:'8px' }}>
              <span style={{ position:'absolute', inset:0, borderRadius:'50%', background:'#34d399', opacity:0.75, animation:'loginPulseDot 2s ease-in-out infinite', display:'block' }}/>
              <span style={{ position:'relative', width:'8px', height:'8px', borderRadius:'50%', background:'#34d399', display:'inline-block' }}/>
            </span>
            Sistema online
          </span>
        </div>

        {/* Cena */}
        <div style={{ position:'relative', zIndex:10, flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'32px 0' }}>
          <VideoMeetScene/>
        </div>

        {/* Narrativa + features */}
        <div style={{ position:'relative', zIndex:10 }}>
          <div style={{ animation:'loginRise 600ms ease-out both' }}>
            <h2 style={{ fontSize:'36px', fontWeight:700, lineHeight:1.05, letterSpacing:'-0.02em', margin:0 }}>
              Sua reunião,<br/>
              <span key={idx} style={{
                display:'inline-block',
                background:'linear-gradient(90deg,#F59E0B,#fbbf24,#a78bfa)',
                WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
                backgroundClip:'text', animation:'loginFadeIn 0.5s ease-out',
              }}>
                {word}
              </span>
            </h2>
            <p style={{ marginTop:'12px', maxWidth:'400px', fontSize:'13px', color:'rgba(255,255,255,0.6)', lineHeight:1.6, margin:'12px 0 0' }}>
              Conecte sua equipe com vídeo HD, compartilhamento de tela e criptografia — tudo no seu servidor.
            </p>
          </div>

          <div style={{ marginTop:'20px', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{
                borderRadius:'12px', border:'1px solid rgba(255,255,255,0.1)',
                background:'rgba(255,255,255,0.04)', padding:'12px', backdropFilter:'blur(8px)',
              }}>
                <div style={{ fontSize:'20px', marginBottom:'6px' }}>{f.icon}</div>
                <div style={{ fontSize:'14px', fontWeight:600 }}>{f.title}</div>
                <div style={{ marginTop:'4px', fontSize:'11px', color:'rgba(255,255,255,0.55)' }}>{f.subtitle}</div>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* ── MODAL SENHA PARA INICIAR REUNIÃO ── */}
      {showPwModal && (
        <div onClick={() => setShowPwModal(false)} style={{
          position:'fixed', inset:0, zIndex:9999,
          background:'rgba(0,0,0,0.6)', backdropFilter:'blur(6px)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width:'100%', maxWidth:'360px', borderRadius:'16px',
            border:'1px solid rgba(255,255,255,0.12)',
            background:'hsl(222,47%,10%)', padding:'28px',
            boxShadow:'0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <h3 style={{ margin:'0 0 6px', fontSize:'18px', fontWeight:700, color:'white' }}>
              {meetingMode === 'later' ? 'Agendar reunião' : 'Senha de acesso'}
            </h3>
            <p style={{ margin:'0 0 18px', fontSize:'13px', color:'rgba(255,255,255,0.55)' }}>
              {meetingMode === 'later'
                ? 'Defina a data e hora da reunião e digite a senha.'
                : 'Digite a senha para criar uma nova reunião.'}
            </p>

            {meetingMode === 'later' && (
              <div style={{ display:'flex', gap:'10px', marginBottom:'14px' }}>
                <div style={{ flex:1 }}>
                  <label style={{ display:'block', fontSize:'11px', color:'rgba(255,255,255,0.5)', marginBottom:'6px', fontWeight:600 }}>
                    Data
                  </label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width:'100%', padding:'10px 12px', borderRadius:'10px',
                      border:'1px solid rgba(255,255,255,0.15)',
                      background:'rgba(255,255,255,0.07)', color:'white',
                      fontSize:'13px', outline:'none', boxSizing:'border-box',
                      fontFamily:'inherit', colorScheme:'dark',
                    }}
                  />
                </div>
                <div style={{ flex:1 }}>
                  <label style={{ display:'block', fontSize:'11px', color:'rgba(255,255,255,0.5)', marginBottom:'6px', fontWeight:600 }}>
                    Hora
                  </label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    style={{
                      width:'100%', padding:'10px 12px', borderRadius:'10px',
                      border:'1px solid rgba(255,255,255,0.15)',
                      background:'rgba(255,255,255,0.07)', color:'white',
                      fontSize:'13px', outline:'none', boxSizing:'border-box',
                      fontFamily:'inherit', colorScheme:'dark',
                    }}
                  />
                </div>
              </div>
            )}

            <input
              autoFocus
              type="password"
              placeholder="Senha"
              value={pwInput}
              onChange={(e) => { setPwInput(e.target.value); setPwError(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmStart(); }}
              style={{
                width:'100%', padding:'10px 14px', borderRadius:'10px',
                border: pwError ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.15)',
                background:'rgba(255,255,255,0.07)', color:'white',
                fontSize:'13px', outline:'none', boxSizing:'border-box',
                fontFamily:'inherit',
              }}
            />
            {pwError && (
              <p style={{ margin:'8px 0 0', fontSize:'12px', color:'#ef4444' }}>
                Senha incorreta.
              </p>
            )}
            {scheduleError && (
              <p style={{ margin:'8px 0 0', fontSize:'12px', color:'#ef4444' }}>
                Preencha a data e hora da reunião.
              </p>
            )}
            <div style={{ display:'flex', gap:'10px', marginTop:'18px' }}>
              <button onClick={() => setShowPwModal(false)} style={{
                flex:1, padding:'10px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.15)',
                background:'transparent', color:'rgba(255,255,255,0.7)',
                fontSize:'13px', fontWeight:600, cursor:'pointer',
              }}>
                Cancelar
              </button>
              <button onClick={confirmStart} style={{
                flex:1, padding:'10px', borderRadius:'10px', border:'none',
                background:'linear-gradient(135deg,#F59E0B,#fbbf24)', color:'#000',
                fontSize:'13px', fontWeight:700, cursor:'pointer',
                boxShadow:'0 4px 20px rgba(245,158,11,0.4)',
              }}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={
      <div style={{ display:'flex', minHeight:'100vh', alignItems:'center', justifyContent:'center', background:'#ffffff', color:'#1a1a1a', fontSize:'14px' }}>
        Carregando...
      </div>
    }>
      <HomeContent/>
    </Suspense>
  );
}
